/* eslint-disable react/display-name */
import React, { ReactNode } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { formatDateString, formatDayTimeString } from '~libs/dayjs';

import { Option } from '~components/form-fields/inputs/Select';

import { ColumnType, EditableColumnConfig } from '../types';
import {
  checkboxEditor,
  currencyEditor,
  dateEditor,
  multiSelectEditor,
  numberEditor,
  selectEditor,
  textEditor,
} from './columnEditors';
import { checkboxCellRender, currencyCellRender, genericCellRender } from './columnRenderers';

type EditorProps<T = any> = {
  value: any;
  row: { original: T };
  onChange: (val: any) => void;
};

function getRender(
  type: ColumnType,
  id: string,
  options: Option[],
  props: { [key: string]: any },
): (value: any, className?: string, row?: any) => ReactNode {
  switch (type) {
    case 'select':
      return genericCellRender((value) => options.find((opt) => opt.value === value)?.label ?? '');
    case 'multi-select':
      return genericCellRender((value) => {
        if (!value || !Array.isArray(value)) return '';
        return [...value]
          .sort((a, b) => a - b)
          .map((val) => options.find((opt) => opt.value === val)?.label || val)
          .join(', ');
      });
    case 'checkbox':
      return checkboxCellRender();
    case 'number':
      return (value, className, row) => {
        return genericCellRender((_value) => {
          if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '';
          const unit = typeof props.unit === 'function' ? props.unit(row) : props.unit;
          const numValue = Number(value);
          return `${numValue}${unit ? ` ${unit}` : ''}`;
        })(value, className);
      };
    case 'date':
      return genericCellRender((value) => (dayjs(value).isValid() ? formatDateString(value) : 'N/A'));
    case 'datetime':
      return genericCellRender((value) => (dayjs(value).isValid() ? formatDayTimeString(value) : 'N/A'));
    case 'text':
      return genericCellRender((value) => value ?? '');
    case 'currency':
      return (value, className, row) =>
        currencyCellRender(id, props.noDecimals ?? true, props.zeroDisplay)(value, className, row);
    case 'link':
      return genericCellRender((value) => value ?? '', false);
    case 'custom':
      return (_value, _className, row) => props.meta?.renderCustom?.(false, row) ?? null;
    default:
      return () => '';
  }
}

function getEditor(
  type: ColumnType,
  id: string,
  options: Option[] = [],
  props: { [key: string]: any } = {},
): ((editorProps: any) => React.ReactElement) | undefined {
  const baseEditor = (() => {
    switch (type) {
      case 'select':
        return selectEditor(options, props.filterOptions, props.disabledOnEmpty);
      case 'multi-select':
        return multiSelectEditor(options, props.filterOptions);
      case 'checkbox':
        return checkboxEditor(id, props.tooltip ?? '');
      case 'number':
        return numberEditor(id, props.min ?? 0, props.max ?? undefined);
      case 'date':
        return dateEditor(id, props.minDate, props.maxDate);
      case 'text':
        return textEditor(id);
      case 'currency':
        return currencyEditor(id, props.noDecimals ?? true);
      case 'custom':
        return ({ row, onChange }: EditorProps) => props.meta?.renderCustom?.(true, row, onChange) ?? null;
      default:
        return undefined;
    }
  })();

  if (!baseEditor) return undefined;

  const WrappedEditor = function EditorWrapper({ row, value, onChange }: EditorProps): React.ReactElement {
    const isDisabled = typeof props.disabled === 'function' ? props.disabled(row) : props.disabled;

    if (isDisabled) {
      const currentValue = (row?.original as any)?.[id] ?? (row as any)?.[id] ?? value;
      const render = getRender(type, id, options, props);
      const content = render(currentValue, 'ps-2', row?.original ?? row);
      return React.createElement('div', { className: 'ps-2' }, content);
    }

    return baseEditor({ row, value, onChange });
  };
  return WrappedEditor;
}

export const buildColumn = ({
  id,
  header,
  type,
  editable = true,
  sortable,
  style,
  options = [],
  props = {},
  accessorFn,
}: EditableColumnConfig): ColumnDef<any> => {
  const render = getRender(type, id, options, props);
  const editComponent = editable ? getEditor(type, id, options, props) : undefined;

  return {
    id,
    ...(accessorFn ? { accessorFn } : { accessorKey: id }),
    ...(sortable === false ? { enableSorting: false } : {}),
    header,
    cell: (cellContext) => {
      const value = cellContext.getValue();
      const row = cellContext.row.original;

      let styleCell = style?.cell;
      if (typeof styleCell === 'function') {
        styleCell = styleCell(row);
      }
      return render(value, styleCell, row);
    },
    meta: {
      style: {
        cell: style?.baseCell ?? style?.cell,
        header: classNames(style?.header),
      },
      ...(editable ? { editComponent } : { readOnly: true }),
      ...props?.meta,
    },
  };
};
